import { useState, useEffect } from 'react';
import { X, Globe, MapPin, Calendar, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { useUserData } from './UserDataContext';
import { UserService } from '../services/api';
import { calculateAge } from './utils/timeFormat';
import { Loader2 } from 'lucide-react';

interface DiscoverFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterOptions) => void;
  currentFilters: FilterOptions;
}

export interface FilterOptions {
  location: 'international' | 'same-country';
  ageRange: [number, number];
  interests: string[];
  tribes: string[];
}

// Real, matchable identity tags (all exist in identityData) shown alongside the
// live trending tags. The previous placeholders ('Tech','Gaming','Science',…)
// are NOT real identity tags, so selecting them matched zero users and made the
// Identities filter look broken.
const availableInterests = [
  'Yoruba', 'Igbo', 'Hausa', 'Akan', 'Amhara', 'Zulu', 'Somali', 'Swahili',
  'Afrobeats', 'Amapiano', 'Christianity', 'Islam',
];

export function DiscoverFilterModal({ isOpen, onClose, onApplyFilters, currentFilters }: DiscoverFilterModalProps) {
  const { userProfile } = useUserData();

  // Compute age live from DOB. Fail-closed: null (absent/malformed DOB) → treat as minor.
  // Never fall back to the cached age field or to a hardcoded adult default.
  const isUnder18 = (() => {
    const computed = calculateAge(userProfile.dateOfBirth);
    return computed === null || computed < 18;
  })();

  // Set age limits based on user's age
  const minAge = isUnder18 ? 13 : 18;
  const maxAge = isUnder18 ? 17 : 100;

  const [filters, setFilters] = useState<FilterOptions>({
    location: currentFilters.location || 'international',
    ageRange: currentFilters.ageRange || (isUnder18 ? [13, 17] : [18, 30]),
    interests: currentFilters.interests || [],
    tribes: [],
  });

  const [trendingTags, setTrendingTags] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  useEffect(() => {
    async function loadTags() {
      if (!isOpen) return;
      setIsLoadingTags(true);
      try {
        const viewerAgeGroup = isUnder18 ? 'minor' : 'adult';
        const tags = await UserService.fetchTrendingTags(viewerAgeGroup);
        setTrendingTags(tags);
      } catch (err) {
        console.error("Failed to fetch trending tags:", err);
      } finally {
        setIsLoadingTags(false);
      }
    }
    loadTags();
  }, [isOpen]);

  // Combine dynamic trending tags first, then static fallback ones
  const allAvailableInterests = Array.from(new Set([
    ...trendingTags,
    ...availableInterests
  ]));

  useEffect(() => {
    // Reset age range if user's age category changes
    if (filters.ageRange && isUnder18 && (filters.ageRange[0] < 13 || filters.ageRange[1] > 17)) {
      setFilters(prev => ({ ...prev, ageRange: [13, 17] }));
    } else if (filters.ageRange && !isUnder18 && filters.ageRange[0] < 18) {
      setFilters(prev => ({ ...prev, ageRange: [18, 30] }));
    }
  }, [isUnder18]);

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    setFilters({
      location: 'international',
      ageRange: isUnder18 ? [13, 17] : [18, 30],
      interests: [],
      tribes: [],
    });
  };

  const toggleInterest = (interest: string) => {
    setFilters(prev => {
      const isSelected = prev.interests.includes(interest);
      if (!isSelected && prev.interests.length >= 3) return prev;
      return {
        ...prev,
        interests: isSelected
          ? prev.interests.filter(i => i !== interest)
          : [...prev.interests, interest]
      };
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center px-4 pt-4 pb-24 md:p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="relative w-full max-w-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] rounded-t-3xl md:rounded-3xl overflow-hidden max-h-[90vh] flex flex-col modal-shadow"
        >
          {/* Header */}
          <div
            className="flex items-center justify-between p-6 border-b border-black/10 dark:border-white/10 shrink-0"
            style={{ paddingTop: 'max(48px, calc(env(safe-area-inset-top) + 24px))' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white dark:bg-white/10 flex items-center justify-center shadow-sm">
                <Sparkles className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-black dark:text-white">
                  Filters
                </h2>
                <p className="text-xs text-[#666666] dark:text-[#E5E5E5]">
                  {isUnder18 ? 'Ages 13-17' : 'Ages 18+'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-black/5 dark:bg-white/10 text-black dark:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 min-h-0 bg-[#f2deb3] dark:bg-[#1a1a1a] overflow-y-auto custom-scrollbar">
            <div className="p-6 space-y-6">
              {/* Age Safety Notice */}
              {isUnder18 && (
                <div className="p-4 rounded-xl bg-[#F39C12]/10 border border-[#F39C12]">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#F39C12] flex items-center justify-center shrink-0">
                      <Calendar className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-black dark:text-white mb-1">Safety First</h3>
                      <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">
                        For your safety, you can only connect with people aged 13-17. Once you turn 18,
                        you'll be able to connect with the 18+ community.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Location Filter */}
              <div>
                <h3 className="text-lg text-black dark:text-white mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                  Location Preference
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, location: 'international' }))}
                    className={`p-4 rounded-xl border-2 transition-all ${filters.location === 'international'
                      ? 'border-[#F39C12] dark:border-[#4478ea] bg-[#F39C12]/10 dark:bg-[#4478ea]/10'
                      : 'border-black/5 dark:border-white/10 bg-white/50 dark:bg-[#2a2a2a]'
                      }`}
                  >
                    <Globe className={`w-6 h-6 mb-2 mx-auto ${filters.location === 'international' ? 'text-[#F39C12] dark:text-[#4478ea]' : 'text-[#666666] dark:text-[#E5E5E5]'
                      }`} />
                    <p className="text-black dark:text-white">International</p>
                    <p className="text-xs text-[#666666] dark:text-[#E5E5E5] mt-1">Meet friends worldwide</p>
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, location: 'same-country' }))}
                    className={`p-4 rounded-xl border-2 transition-all ${filters.location === 'same-country'
                      ? 'border-[#F39C12] dark:border-[#4478ea] bg-[#F39C12]/10 dark:bg-[#4478ea]/10'
                      : 'border-black/5 dark:border-white/10 bg-white/50 dark:bg-[#2a2a2a]'
                      }`}
                  >
                    <MapPin className={`w-6 h-6 mb-2 mx-auto ${filters.location === 'same-country' ? 'text-[#F39C12] dark:text-[#4478ea]' : 'text-[#666666] dark:text-[#E5E5E5]'
                      }`} />
                    <p className="text-black dark:text-white">My Country</p>
                    <p className="text-xs text-[#666666] dark:text-[#E5E5E5] mt-1">
                      {userProfile.home_residence || userProfile.location || userProfile.country || 'Same country'}
                    </p>
                  </button>
                </div>
              </div>

              {/* Age Range Filter */}
              <div>
                <h3 className="text-lg text-black dark:text-white mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                  Age Range
                </h3>
                <div className="p-5 rounded-xl bg-white/50 dark:bg-[#2a2a2a] border border-black/5 dark:border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl text-black dark:text-white">
                      {filters.ageRange[0]}
                    </span>
                    <span className="text-[#666666] dark:text-[#E5E5E5]">to</span>
                    <span className="text-2xl text-black dark:text-white">
                      {filters.ageRange[1] === maxAge && !isUnder18 ? '100+' : filters.ageRange[1]}
                    </span>
                  </div>
                  <Slider
                    min={minAge}
                    max={maxAge}
                    step={1}
                    value={filters.ageRange}
                    onValueChange={(value: number[]) => setFilters(prev => ({ ...prev, ageRange: value as [number, number] }))}
                    className="w-full"
                  />
                  <div className="flex items-center justify-between mt-3 text-xs text-[#666666] dark:text-[#E5E5E5]">
                    <span>{minAge}</span>
                    <span>{isUnder18 ? maxAge : '100+'}</span>
                  </div>
                </div>
              </div>

              {/* Identities Filter */}
              <div>
                <h3 className="text-lg text-black dark:text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                  Identities ({filters.interests.length}/3)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {isLoadingTags && (
                    <div className="w-full flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 text-[#F39C12] animate-spin" />
                      <span className="ml-2 text-xs text-[#666666] dark:text-[#E5E5E5]">Loading trending tags...</span>
                    </div>
                  )}
                  {allAvailableInterests.map((interest) => (
                    <button
                      key={interest}
                      onClick={() => toggleInterest(interest)}
                      className={`px-4 py-2 rounded-full text-sm transition-all flex items-center gap-2 ${filters.interests.includes(interest)
                        ? 'bg-[#F39C12] dark:bg-[#4478ea] text-white shadow-md'
                        : 'bg-white/50 dark:bg-[#2a2a2a] text-black dark:text-white border border-black/5 dark:border-white/10 hover:border-[#F39C12]/50'
                        }`}
                    >
                      {trendingTags.includes(interest) && (
                         <Sparkles className="w-3 h-3 text-[#F39C12] dark:text-[#4478ea]" />
                      )}
                      {interest}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-black/10 dark:border-white/10 shrink-0 flex gap-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)' }}>
            <Button
              variant="outline"
              onClick={handleReset}
              className="flex-1 h-12 border-2 border-black/10 dark:border-white/10 text-black dark:text-white hover:border-[#F39C12] hover:bg-[#F39C12]/10"
            >
              Reset
            </Button>
            <Button
              onClick={handleApply}
              className="flex-1 h-12 bg-[#F39C12] dark:bg-[#4478ea] text-white hover:opacity-90 font-bold"
            >
              Apply Filters
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
